import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { useAuth } from '../contexts/AuthContext';

const Navbar: React.FC = () => {
  const { user: userContextUser, clearUser, getImmediateSubscription } = useContext(UserContext);
  const { user: authUser, logout: authLogout } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Use either context's user
  const user = userContextUser || authUser;
  
  // Get immediate subscription status from localStorage
  const immediateSubscription = getImmediateSubscription ? getImmediateSubscription() : {
    status: localStorage.getItem('subscription_status') || 'none',
    plan: localStorage.getItem('subscription_plan') || 'none'
  };

  const handleLogout = () => {
    if (clearUser) clearUser(); // UserContext logout
    if (authLogout) authLogout(); // AuthContext logout
    navigate('/login');
  };

  // Get status badge colors for dark theme
  const getStatusBadgeClass = () => {
    const status = (user?.subscription || immediateSubscription)?.status;
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'canceled':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'past_due':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'trialing':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  // Helper function to normalize plan name (only Free or Premium)
  const normalizePlanName = (plan: any): string => {
    if (!plan || plan === 'none') {
      return 'Free';
    }
    
    // If it's an object with a name property
    if (typeof plan === 'object' && plan.name) {
      // Check if it's any premium variant
      const planName = plan.name.toLowerCase();
      if (planName.includes('premium') || planName.includes('pro') || planName.includes('basic')) {
        return 'Premium';
      }
      return plan.name;
    }
    
    // If it's a string, check if it's a premium variant
    if (typeof plan === 'string') {
      const planLower = plan.toLowerCase();
      // If it's any paid plan (BASIC, PRO, PREMIUM, etc.), consider it Premium
      if (planLower.includes('premium') || planLower.includes('pro') || planLower.includes('basic') || 
          plan === 'BASIC' || plan === 'PRO' || plan === 'PREMIUM') {
        return 'Premium';
      }
      return 'Free';
    }
    
    return 'Free';
  };

  const subscription = user?.subscription || immediateSubscription;
  const planName = normalizePlanName(subscription?.plan);
  const isAdmin = user?.role === 'admin';

  return (
    <nav 
      className="border-b border-white/10"
      style={{ background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <img src="/images/logo.png" alt="AdSmash Logo" className="h-10 md:h-12" />
            </Link>
            {user && (
              <div className="hidden md:block ml-10">
                <div className="flex items-baseline space-x-4">
                  <Link to="/dashboard" className="px-3 py-2 rounded-md text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors">
                    Dashboard
                  </Link>
                  {isAdmin && (
                    <Link to="/admin/users" className="px-3 py-2 rounded-md text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors">
                      Members
                    </Link>
                  )}
                  <Link to="/billing" className="px-3 py-2 rounded-md text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors">
                    Billing
                  </Link>
                  <Link to="/settings" className="px-3 py-2 rounded-md text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors">
                    Settings
                  </Link>
                </div>
              </div>
            )}
          </div>
          <div className="hidden md:block">
            <div className="ml-4 flex items-center md:ml-6">
              {user ? (
                <div className="flex items-center gap-4">
                  <div className="flex flex-col items-end mr-2">
                    <span className="text-white text-sm">{user.email}</span>
                    <span className={`inline-block py-1 px-3 rounded-full text-xs font-semibold border mt-1 ${getStatusBadgeClass()}`}>
                      {planName}
                    </span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 border border-white/20 rounded-md text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                  <Link to="/login" className="px-4 py-2 rounded-md text-sm font-medium text-white/70 hover:text-white transition-colors">
                    Login
                  </Link>
                </div>
              )}
            </div>
          </div>
          <div className="-mr-2 flex md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-white/70 hover:text-white hover:bg-white/10 focus:outline-none transition-colors"
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-white/10">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                 {user && (
                   <>
                     <Link to="/dashboard" className="block px-3 py-2 rounded-md text-base font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors">
                       Dashboard
                     </Link>
                     {isAdmin && (
                       <Link to="/admin/users" className="block px-3 py-2 rounded-md text-base font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors">
                         Members
                       </Link>
                     )}
                     <Link to="/billing" className="block px-3 py-2 rounded-md text-base font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors">
                       Billing
                     </Link>
                     <Link to="/settings" className="block px-3 py-2 rounded-md text-base font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors">
                       Settings
                     </Link>
                   </>
                 )}
          </div>
          <div className="pt-4 pb-3 border-t border-white/10">
            {user ? (
              <div className="flex items-center px-5">
                <div className="ml-3 w-full">
                  <div className="text-base font-medium leading-none text-white">{user.email}</div>
                  <div className="mt-2 mb-3">
                    <span className={`inline-block py-1 px-3 rounded-full text-xs font-semibold border ${getStatusBadgeClass()}`}>
                      {planName}
                    </span>
                  </div>
                  <button onClick={handleLogout} className="mt-3 px-3 py-2 rounded-md text-base font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors w-full text-left">
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-2 space-y-1">
                <Link to="/login" className="block px-3 py-2 rounded-md text-base font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors">
                  Login
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar; 
